import React from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="min-h-screen bg-neutral-50 px-0">
      <div className="w-full py-8">
        {/* Hero Section */}
        <div className="page-heading-container hairline min-h-[32vh] md:min-h-[48vh] relative mb-8">
          <div className="page-heading-content absolute bottom-0 left-2 right-2 md:left-4 md:right-4">
            <h1 className="font-sans text-hero text-neutral-900 tracking-tight font-medium">
              RECIPES THAT KEEP LIVING
            </h1>
            <p className="page-heading-subtitle font-mono uppercase tracking-wider text-neutral-900 opacity-60 mt-0 text-sm md:text-lg">
              BUILT FOR FAMILY RECIPES • SHARE • FORK • COMPARE • SHARE
            </p>
          </div>
        </div>

        {/* Hero Content */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8 text-center">
          <p className="text-lg text-neutral-900 mb-8 leading-relaxed">
            What started as sharing recipes with my grandma became a way to preserve, iterate, and celebrate the stories behind every family dish.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/"
              className="px-6 py-3 font-mono text-sm uppercase tracking-wider text-white hover:opacity-60 transition-opacity border-none"
              style={{ backgroundColor: '#1b263b' }}
            >
              Start Cooking
            </Link>
            <Link 
              to="/"
              className="px-6 py-3 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
            >
              Explore Recipes
            </Link>
          </div>
        </div>

        {/* Our Why - The Grandma Story */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8 text-center">
          <h2 className="font-sans text-3xl font-medium mb-6" style={{ color: '#1b263b' }}>
            Our Why
          </h2>
          
          <div className="space-y-6 text-neutral-900 leading-relaxed">
            <p>
              It all started with my grandma's chocolate chip cookies. I wanted to share the recipe, but texting PDFs and screenshots felt clunky. When she suggested adding a pinch of cinnamon or I wanted to try brown butter, we'd lose track of the original version.
            </p>
            
            <p>
              We needed something better—a way to preserve the original while celebrating the variations that make each family's version special.
            </p>
          </div>
          
          <div className="mt-8 p-6" style={{ backgroundColor: '#EEEEE7' }}>
            <blockquote className="text-lg italic text-neutral-900 leading-relaxed">
              "Every family has their own way of making the same dish. That's what makes it ours."
            </blockquote>
            <cite className="block mt-3 font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
              — My Grandma
            </cite>
          </div>
        </div>

        {/* How 4rked Works */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8 text-center">
          <h2 className="font-sans text-3xl font-medium mb-8" style={{ color: '#1b263b' }}>
            How 4rked Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-neutral-50 p-4 hairline">
              <h3 className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">1. Share</h3>
              <p className="text-sm text-neutral-900">Create a clean, readable recipe page that's easy to scan and follow.</p>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <h3 className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">2. Fork</h3>
              <p className="text-sm text-neutral-900">Try a twist—add ingredients, adjust quantities, or swap techniques.</p>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <h3 className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">3. Compare</h3>
              <p className="text-sm text-neutral-900">See exactly what changed with visual diffs highlighting additions and removals.</p>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <h3 className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">4. Share</h3>
              <p className="text-sm text-neutral-900">Send the link back to family and keep the conversation going.</p>
            </div>
          </div>
          
          {/* Diff Preview Demo */}
          <div className="bg-neutral-50 p-4 hairline">
            <h3 className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">Comparison Preview</h3>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <div className="font-mono uppercase tracking-wider text-neutral-900 mb-2">Original</div>
                <div className="space-y-1">
                  <div className="p-2 bg-neutral-0 hairline">2 cups flour</div>
                  <div className="p-2 bg-neutral-0 hairline">1 cup sugar</div>
                  <div className="p-2 bg-neutral-0 hairline">1 tsp vanilla</div>
                </div>
              </div>
              <div>
                <div className="font-mono uppercase tracking-wider text-neutral-900 mb-2">Updated</div>
                <div className="space-y-1">
                  <div className="p-2 bg-neutral-0 hairline">2 cups flour</div>
                  <div className="p-2 bg-neutral-0 hairline">1 cup brown sugar</div>
                  <div className="p-2 bg-neutral-0 hairline">1 tsp vanilla</div>
                  <div className="p-2 border-l-2" style={{ backgroundColor: '#EEEEE7', borderLeftColor: '#1b263b' }}>
                    + 1 tsp cinnamon
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Made for Families */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8 text-center">
          <h2 className="font-sans text-3xl font-medium mb-6" style={{ color: '#1b263b' }}>
            Made for Families
          </h2>
          
          <div className="space-y-6 text-neutral-900 leading-relaxed">
            <p>
              Every family has their own version of the same recipe. Grandma's secret ingredient, Aunt Sarah's timing adjustment, your modern twist—each variation tells a story.
            </p>
            
            <p>
              Our clean, readable layout surfaces the essentials: course, time, and difficulty. No more hunting through paragraphs to find what you need. Just clear, scannable information that gets you cooking faster.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8 text-center">
          <h2 className="font-sans text-3xl font-medium mb-8" style={{ color: '#1b263b' }}>
            Our Values
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-neutral-50 p-4 hairline">
              <h3 className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">Keep the original safe</h3>
              <p className="text-sm text-neutral-900">Every fork preserves the source recipe, so nothing gets lost in translation.</p>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <h3 className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">Celebrate variations</h3>
              <p className="text-sm text-neutral-900">Every twist and tweak is part of the story, not a mistake to fix.</p>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <h3 className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">Make it easy to share</h3>
              <p className="text-sm text-neutral-900">One link, clean layout, no apps to download—just cooking.</p>
            </div>
          </div>
        </div>

        {/* Gentle CTA */}
        <div className="bg-neutral-0 hairline p-4 mb-8">
          <h2 className="font-sans text-hero text-neutral-900 tracking-tight font-medium mb-2">
            READY TO START?
          </h2>
          
          <p className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">
            Import your favorite family recipe today and see how it evolves with each generation.
          </p>
          
          <Link 
            to="/"
            className="inline-block px-6 py-3 font-mono text-sm uppercase tracking-wider text-white hover:opacity-60 transition-opacity border-none"
            style={{ backgroundColor: '#1b263b' }}
          >
            Create New Recipe
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About; 